import {
  Injectable,
  OnInit,
  OnDestroy,
  Inject,
  Injector,
  Input,
  ChangeDetectorRef,
} from '@angular/core';
import { NgrxErrorStateMatcher } from './ngrx-error-state-matcher';
import { FORM_FIELD_APPEARANCE } from './tokens';
import {
  FormControlValueTypes,
  Unboxed,
  FormControlState,
} from 'ngrx-forms';
import { Observable } from 'rxjs';
import { DxFormControl } from './form-control';
import { AbstractFormDefinition } from './abstract-form-definition';
import { FormsSources } from './forms-sources';
import { AbstractInputComponent } from './abstract-input.component';
import { Store } from '@ngrx/store';

export interface OnControlSet {
  dxOnControlSet(): void;
}

export const dxOnControlSet = 'dxOnControlSet';

@Injectable()
export abstract class AbstractControlComponent<
  FormValue extends FormControlValueTypes,
  FormDefinition extends AbstractFormDefinition<
    FormValue
  > = AbstractFormDefinition<FormValue>,
  Value extends Unboxed<FormValue> = Unboxed<FormValue>
> extends AbstractInputComponent<FormValue> implements OnInit, OnDestroy {
  public get dxFormControl(): DxFormControl<FormValue> {
    return this._dxFormControl;
  }

  public set dxFormControl(value: DxFormControl<FormValue>) {
    this._dxFormControl = value;
    if (this[dxOnControlSet] && typeof this[dxOnControlSet] === 'function') {
      console.log(
        '[AbstractControlComponent::(set)dxFormControl] call OnControlSet',
        this._dxFormControl.__controlId,
        this._dxFormControl
      );
      this[dxOnControlSet]();
    } else {
      console.log(
        '[AbstractControlComponent::(set)dxFormControl] OnControlSet is not defined',
        this._dxFormControl.__controlId,
        this._dxFormControl
      );
    }
  }

  @Input('ngrxFormControlState')
  public set ngrxFormControl(value: FormControlState<FormValue>) {
    this.formControl.ngrxFormControl = value;
    this.state = value;
    const formId = value.id.match(/^[^\.]+/)[0];
    this.processUserDefinedProperties(value.userDefinedProperties);
    if (!this.formDefinition || this.formDefinition.formId !== formId) {
      const controlPath = value.id
        .match(/^[^\.]+\.(.*)$/)[1]
        .split('.')
        .join('/');
      this.formDefinition = this.formsSources.getForm(formId);
      this.dxFormControl = this.formDefinition.getControl(controlPath);
    }
    this.cdr.markForCheck();
  }

  private _dxFormControl: DxFormControl<FormValue>;
  public formDefinition: FormDefinition;
  public state: FormControlState<FormValue>;

  public get valid(): boolean {
    return Object.keys(this.state.errors || {}).length === 0;
  }

  public set valid(value: boolean) {
    console.warn('[AbstractControlComponent] set >valid< is not supported');
  }

  public get invalid(): boolean {
    return !this.valid;
  }

  public set invalid(value: boolean) {
    console.warn('[AbstractControlComponent] set >invalid< is not supported');
  }

  public get placeholder(): string {
    return this._dxFormControl && this._dxFormControl.placeholder;
  }

  public set placeholder(value: string) {
    if (this._dxFormControl) {
      this._dxFormControl.placeholder = value;
    }
  }

  public get fxHide(): boolean {
    return this._dxFormControl && this._dxFormControl.fxHide;
  }

  public set fxHide(value: boolean) {
    if (this._dxFormControl) {
      this._dxFormControl.fxHide = value;
    }
  }

  public get fxFlex(): string {
    return this._dxFormControl && this._dxFormControl.fxFlex;
  }

  public set fxFlex(value: string) {
    if (this._dxFormControl) {
      this._dxFormControl.fxFlex = value;
    }
  }

  public get readonly(): boolean {
    return this._dxFormControl && this._dxFormControl.readonly;
  }

  public set readonly(value: boolean) {
    if (this._dxFormControl) {
      this._dxFormControl.readonly = value;
    }
  }

  public get label(): string {
    return this._dxFormControl && this._dxFormControl.label;
  }

  public set label(value: string) {
    if (this._dxFormControl) {
      this._dxFormControl.label = value;
    }
  }

  public get disabled(): boolean {
    return this._dxFormControl && this._dxFormControl.disabled;
  }

  public set disabled(value: boolean) {
    if (this._dxFormControl) {
      this._dxFormControl.disabled = value;
    }
  }

  public get required(): boolean {
    return this._dxFormControl && this._dxFormControl.required;
  }

  public set required(value: boolean) {
    if (this._dxFormControl) {
      this._dxFormControl.required = value;
    }
  }

  public get value(): Value {
    return this._dxFormControl && this._dxFormControl.__value;
  }

  public set value(value: Value) {
    if (this._dxFormControl) {
      this._dxFormControl.setValue(value);
    }
  }

  public get value$(): Observable<Value> {
    return this._dxFormControl && this._dxFormControl.__value$;
  }

  public constructor(
    public matcher: NgrxErrorStateMatcher,
    public injector: Injector,
    @Inject(FORM_FIELD_APPEARANCE) appearance: string,
    public formsSources: FormsSources,
    public store$: Store<any>,
    public cdr: ChangeDetectorRef
  ) {
    super(matcher, appearance);
  }
}
